import { forwardRef, ReactNode } from "react";
import { Placement, Tooltip } from "../Tooltip";
import "./LevelBadge.css";

export type LevelBadgeLevel = 0 | 1 | 2 | 3 | 4 | 5;

export const isValidLevel = (n: number): n is LevelBadgeLevel =>
  [0, 1, 2, 3, 4, 5].includes(n);

export interface LevelBadgeProps {
  level: LevelBadgeLevel;
  text?: string;
  prefix?: string;
  tooltipContent?: ReactNode;
  tooltipPlacement?: Placement;
  className?: string;
  "data-testid"?: string;
}

const getDefaultLabel = (level: LevelBadgeLevel, prefix: string): string =>
  `${prefix}${level}`;

export const LevelBadge = forwardRef<HTMLSpanElement, LevelBadgeProps>(
  (
    {
      level,
      text,
      prefix = "LV",
      tooltipContent,
      tooltipPlacement = "top",
      className = "",
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const classes = [
      "indigo-level-badge",
      `indigo-level-badge--level-${level}`,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const label = text ?? getDefaultLabel(level, prefix);

    const badge = (
      <span ref={ref} className={classes} data-testid={dataTestId}>
        {label}
      </span>
    );

    if (!tooltipContent) {
      return badge;
    }

    return (
      <Tooltip
        content={tooltipContent}
        placement={tooltipPlacement}
        size="sm"
        offset={[0, 5]}
      >
        {badge}
      </Tooltip>
    );
  },
);

LevelBadge.displayName = "LevelBadge";
